import { useState } from "react";
import { toast } from "sonner";

/**
 * Modal asking the user to confirm marking a deal as Won and structure the
 * engagement details that the Client auto-spawn flow consumes.
 *
 * Props:
 * - dealName: string — shown in headline.
 * - defaultEngagementType, defaultEngagementEndDate: prefill from the deal.
 * - onCancel(): close without changes.
 * - onConfirm({ engagementType, engagementEndDate }): user confirmed —
 *   caller submits PATCH then triggers the WonExplosion overlay.
 */
export default function WonConfirmModal({
  dealName,
  defaultEngagementType,
  defaultEngagementEndDate,
  onCancel,
  onConfirm,
}) {
  const [type, setType] = useState(defaultEngagementType || "");
  const [endDate, setEndDate] = useState((defaultEngagementEndDate || "").slice(0, 10));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!type) { toast.error("Pick an engagement type"); return; }
    if (!endDate) { toast.error("Set an engagement end date"); return; }
    setSaving(true);
    await onConfirm({ engagementType: type, engagementEndDate: endDate });
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={saving ? undefined : onCancel} />
      <div
        className="pg-card"
        data-testid="won-confirm-modal"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: "1.75rem 2rem",
          width: "min(520px, 92vw)",
          zIndex: 60,
          borderColor: "var(--pg-accent)",
        }}
      >
        <div className="mb-1">
          <span
            className="pg-badge"
            style={{
              background: "rgba(110, 240, 220, 0.10)",
              color: "var(--pg-accent-deep)",
              border: "1px solid rgba(110, 240, 220, 0.30)",
            }}
          >
            MARKING AS WON
          </span>
        </div>
        <h2 className="text-xl mt-3 mb-2" style={{ fontWeight: 600 }}>
          Lock in the win.
        </h2>
        <p className="text-sm text-dim mb-5">
          Deal: <strong style={{ color: "var(--pg-text)" }}>{dealName}</strong>
        </p>

        <div className="space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-2 uppercase tracking-wider">
              Engagement Type <span style={{ color: "var(--pg-accent)" }}>*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "one_off", label: "One-Off", hint: "Single project, defined end." },
                { value: "recurring", label: "Recurring", hint: "Ongoing engagement." },
              ].map((opt) => {
                const isSel = type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className="block text-left pg-card p-3"
                    data-testid={`won-engagement-${opt.value}`}
                    style={{
                      background: isSel ? "rgba(110, 240, 220, 0.06)" : "var(--pg-surface-2)",
                      borderColor: isSel ? "var(--pg-accent)" : "var(--pg-border)",
                      cursor: "pointer",
                      transition: "background 140ms ease, border-color 140ms ease",
                    }}
                  >
                    <div style={{ color: "var(--pg-text)", fontWeight: 600 }}>{opt.label}</div>
                    <div className="mono text-[11px] text-mute mt-0.5">{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block mono text-[11px] text-mute mb-2 uppercase tracking-wider">
              Engagement End Date <span style={{ color: "var(--pg-accent)" }}>*</span>
            </label>
            <input
              type="date"
              className="pg-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="won-end-date"
            />
            <div className="mono text-[10px] text-mute mt-1">
              {type === "recurring"
                ? "Initial commitment end — renewal tracking starts after this date."
                : "When the work wraps up."}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            className="pg-btn"
            onClick={onCancel}
            disabled={saving}
            data-testid="won-cancel"
          >
            Not yet
          </button>
          <button
            type="button"
            className="pg-btn pg-btn-primary"
            onClick={submit}
            disabled={saving || !type || !endDate}
            data-testid="won-confirm"
          >
            {saving ? "Closing…" : "Mark Won"}
          </button>
        </div>
      </div>
    </>
  );
}
